import React from 'react';
import { Typography } from '@mui/material';
import {
	isAssignedPlannerOrAdmin,
	linkObject,
	toSimpleLink,
	getDetailLink,
} from '../../../lib/helpers/index';
import { linkCustomer, LinkCustomerLinkType } from '../helpers';
import { LinkObjectType } from '../../../lib/helpers/types';
import {
	EventCategory,
	GetProposalSentEventPropsEventFragment,
	UserType,
} from '#types';
import { FlattenedUser } from '../../../types/user';
import { gql } from '@apollo/client';
import { DefaultEventProps } from '../contract/helpers';

export const getProposalSentEventPropsEventFragment = gql`
	fragment getProposalSentEventPropsEvent on Event {
		id
		category
		proposal {
			id
			title
		}
		vendor {
			id
		}
		contact {
			id
			customer {
				id
				clientUser {
					id
					assignedPlanner {
						id
						organization {
							id
						}
					}
				}
			}
		}
	}
`;

export const getProposalSentEventProps = (
	defaultEventProps: DefaultEventProps & {
		event: GetProposalSentEventPropsEventFragment;
	},
	user: FlattenedUser
): DefaultEventProps => {
	const updatedEventProps = { ...defaultEventProps };
	if ( !updatedEventProps.event.proposal ) {
		throw new Error( 'No proposal exists on event' );
	}
	const isResentProposalEvent =
		updatedEventProps.event.category === EventCategory.ProposalResent;
	const detailLink = getDetailLink( {
		object: updatedEventProps.event.proposal,
		type: LinkObjectType.Proposal,
	} );
	const userIsCustomer = user?.isLoggedIn
		? updatedEventProps.event.contact?.customer.id === user.user.id
		: false;
	const userCanActOnProposal =
		userIsCustomer ||
		( isAssignedPlannerOrAdmin(
			user,
			defaultEventProps.event.contact?.customer.clientUser
		) &&
			user?.isLoggedIn &&
			user.userType === UserType.OrgUser &&
			user.organization.id !== updatedEventProps.event.vendor.id );
	const proposalLink = linkObject( updatedEventProps.event.proposal );
	const vendorLink = linkObject( updatedEventProps.event.vendor );
	const clientLink = linkCustomer(
		updatedEventProps.event,
		LinkCustomerLinkType.Contact
	);

	updatedEventProps.icon = 'proposal';
	updatedEventProps.links = [
		{
			type: userCanActOnProposal ? 'primary' : 'secondary',
			text: userCanActOnProposal ? 'Accept' : 'View',
			link: detailLink,
		},
	];
	if ( userIsCustomer ) {
		isResentProposalEvent
			? ( updatedEventProps.message = (
				<Typography className='p--secondary'>
					{ toSimpleLink( {
						link: detailLink,
						text: updatedEventProps.event.proposal.title,
					} ) }{ ' ' }
						has been resent by { vendorLink }.
				</Typography>
			  ) )
			: ( updatedEventProps.message = (
				<Typography className='p--secondary'>
						New{ ' ' }
					{ toSimpleLink( {
						link: detailLink,
						text: 'proposal',
					} ) }{ ' ' }
						from { vendorLink }.
				</Typography>
			  ) );
	}

	// Planner Viewer
	else if (
		isAssignedPlannerOrAdmin(
			user,
			defaultEventProps.event.contact?.customer.clientUser
		)
	) {
		// Viewing assigned client's profile
		if ( defaultEventProps.event.contact?.customer.clientUser ) {
			isResentProposalEvent
				? ( updatedEventProps.message = (
					<Typography>
						{ toSimpleLink( {
							link: detailLink,
							text: updatedEventProps.event.proposal.title,
						} ) }{ ' ' }
							has been resent by { vendorLink }.
					</Typography>
				  ) )
				: ( updatedEventProps.message = (
					<Typography className='p--secondary'>
							New{ ' ' }
						{ toSimpleLink( {
							link: detailLink,
							text: 'proposal',
						} ) }{ ' ' }
							from { vendorLink }.
					</Typography>
				  ) );

			// Viewing homepage feed
		} else {
			// Is in org that sent proposal
			if (
				user?.isLoggedIn &&
				user.userType === UserType.OrgUser &&
				updatedEventProps.event.vendor.id === user.organization.id
			) {
				isResentProposalEvent
					? ( updatedEventProps.message = (
						<Typography>
								Your organization resent { clientLink } a proposal: { proposalLink }
								.
						</Typography>
					  ) )
					: ( updatedEventProps.message = (
						<Typography className='p--secondary'>
								Your organization sent { clientLink } a proposal: { proposalLink }.
						</Typography>
					  ) );

				// Is not in org that sent proposal
			} else {
				isResentProposalEvent
					? ( updatedEventProps.message = (
						<Typography>
							{ vendorLink } resent a proposal to { clientLink }:{ ' ' }
							{ toSimpleLink( {
								link: detailLink,
								text: updatedEventProps.event.proposal.title,
							} ) }
						</Typography>
					  ) )
					: ( updatedEventProps.message = (
						<Typography className='p--secondary'>
							{ clientLink } has a new proposal from { vendorLink }.
						</Typography>
					  ) );
			}
		}
	} else if ( user?.isLoggedIn && user.userType === UserType.OrgUser ) {
		isResentProposalEvent
			? ( updatedEventProps.message = (
				<Typography>
						You resent { clientLink } a proposal: { proposalLink }.
				</Typography>
			  ) )
			: ( updatedEventProps.message = (
				<Typography>
						You sent { clientLink } a proposal: { proposalLink }.
				</Typography>
			  ) );
	}

	return updatedEventProps;
};
